import { observable, action, autorun, computed } from 'mobx'

import { setter }                                from 'mobx-decorators'
import xhr                                       from 'helpers/XHR'
import getFile                                   from 'js-file-download'
import uiStore                                   from 'stores/UiStore'
import getError                                  from 'helpers/ErrorParser'
import { orderBy }                               from 'lodash/fp'

export class DistrictStore {
  @setter
  @observable
  isLoading = false

  @setter
  @observable
  isLoadingUserExport = false

  @setter
  @observable
  isError = false

  @setter
  @observable
  selectedDistrict = null

  // IANA identifier ('America/Chicago'), not `tz` ('CT'). Cached for the session.
  @setter
  @observable
  timeZoneName = null

  @setter
  @observable
  districts = observable.map()

  @setter
  @observable
  type = 'all'

  @setter
  @observable
  modules = null

  @setter
  @observable
  schoolYear = null

  contructor() {
    this.initAutoruns()
  }

  // Computed
  @computed
  get hasErrors() {
    return !this.isLoading && this.isError
  }

  @computed
  get districtsAlphabetical() {
    return orderBy((d) => d.district_name, 'asc')(
      this.districts.values()
    )
  }

  @computed
  get modulesAlphabetical() {
    if (!this.selectedDistrict) return []
    return orderBy((m) => m.name, 'asc')(
      this.selectedDistrict.modules
    )
  }

  @computed
  get canExport() {
    return !this.isLoading && !this.isLoadingUserExport
  }

  @computed
  get isReadyForUpdate() {
    return !!this.selectDistrict && !!this.type && !!this.modules
  }

  /*
   * All autoruns are initialized in this function
   */
  @action
  initAutoruns = () => {
    this.autoErrorNotifier()
  }

  /*
   * This autorun will be ran automatically whenever the [isError] is changed.
   * This will automatically show a notification message to the user
   */
  @action
  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    this.isError.type || 'error'
        })
      }
    })
  }

  @action
  fetchUserExport = async() => {
    if (!this.selectedDistrict) return

    try {
      this.setIsLoadingUserExport(true)
      this.setIsError(false)

      const response = await xhr.get(
        `/districts/user_export/${this.selectedDistrict.id}`,
        { responseType: 'blob' }
      )

      this.fetchUserExportOK(response)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoadingUserExport(false)
    }
  }

  fetchUserExportOK(response) {
    getFile(response.data, 'User_Export.zip')
  }

  @action
  fetchDistricts = async() => {
    try {
      this.setIsLoading(true)
      this.setIsError(false)

      const response = await xhr.get('/districts')

      this.fetchDistrictsOK(response)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  fetchDistrictsOK = (response) => {
    if (this.districts.size !== 0) this.clearData()
    response.data.forEach((d) => this.districts.set(d.id, d))
  }

  @action
  selectDistrict = (id) => {
    this.selectedDistrict = this.districts.get(id)
  }

  @action
  selectSchoolYear = (year) => {
    this.schoolYear = year.target.value
  }

  @action
  addModuleToUsers = async() => {
    if (!this.selectedDistrict) return

    try {
      this.setIsLoading(true)
      this.setIsError(false)

      const formData = new FormData()

      formData.append('symbols', this.modules.join(','))
      formData.append('type', this.type)

      const { data } = await xhr.post(
        `/districts/${this.selectedDistrict.id}/add_module_to_users`,
        formData,
        {
          'Content-Type': 'multipart/form-data'
        }
      )

      this.addModulesOk(data)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  addModulesOk = (data) => {
    {
      data && uiStore.addMessage('Modules successfully added!')
    }
  }

  @action
  removeModuleFromUsers = async() => {
    if (!this.selectedDistrict) return

    try {
      this.setIsLoading(true)
      this.setIsError(false)

      const formData = new FormData()

      formData.append('id', this.selectedDistrict.id)
      formData.append('symbols', this.modules.join(','))
      formData.append('type', this.type)

      const { data } = await xhr.post(
        `/districts/${
          this.selectedDistrict.id
        }/remove_module_from_users`,
        formData,
        {
          'Content-Type': 'multipart/form-data'
        }
      )

      this.removeModulesOk(data)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  removeModulesOk = (data) => {
    {
      data && uiStore.addMessage('Modules successfully removed!')
    }
  }

  @action
  clearData = () => {
    this.districts.clear()
  }

  /*
   * dispose all autoruns here to avoid subtle memory leak issues
   */
  @action
  dispose = () => {
    this.autoErrorNotifier && this.autoErrorNotifier()
  }

  @action
  deletePictureCache = async() => {
    if (!this.selectedDistrict) return

    try {
      this.setIsLoading(true)
      this.setIsError(false)

      await xhr.post(
        `/districts/${this.selectedDistrict.id}/delete_picture_cache`
      )
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  clearCase21 = async() => {
    if (!this.selectedDistrict) return

    const params = { school_year: this.schoolYear }

    try {
      this.setIsLoading(true)
      this.setIsError(false)

      await xhr.post(
        `/districts/${this.selectedDistrict.id}/clear_case21`,
        params
      )

      uiStore.addNotification({
        title:   'Case21',
        message:
          'Processing. There will be a notification in the ' +
          'Integrations Slack Channel when the job is finished.',
        type: 'info'
      })
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  getDistrictTimeZoneName = async (districtId) => {
    if (this.timeZoneName) return this.timeZoneName

    try {
      const res = await xhr.get(`/districts/${districtId}`, {
          params: {
            only: 'tz_name'
          }
        })
      this.setTimeZoneName(res.data.tz_name)
      return res.data.tz_name
    } catch (e) {
      this.setIsError(getError(e))
      console.error(e)
    }
  }

  @action
  getDistrictSalesForceID = async (districtId) => {
    try {
      const res = await xhr.get(`/districts/${districtId}`, {
          params: {
            only: 'salesforce_id'
          }
        })
      return res.data.salesforce_id
    } catch (e) {
      this.setIsError(getError(e))
      console.error(e)
    }
  }
}

export default new DistrictStore()
